import get from 'lodash/get'

import ValidationResults, { ValidationResultDetails } from './ValidationResults'

type FormRules<T> = { [k in keyof T]: IValidationRule }

interface IValidationRule {
  // Additional parameters for the validation method
  args?: any[]

  // Message to display if the rule is not validated
  message?: React.ReactNode

  // Validation method, should return true if the rule is validated
  method: (fieldValue: any, ...args: any[]) => boolean

  // Override where to find the value this rule needs to validate
  source?: string | string[]
}

class FormValidator<T> {
  public rules: FormRules<T>

  public constructor(rules: FormRules<T>) {
    this.rules = rules
  }

  public validate(state: any, ignore_rules: Array<keyof FormRules<T>> = []): ValidationResults<T> {
    const details: ValidationResultDetails<T> = new Map()

    for (const ruleName in this.rules) {
      if (ignore_rules.includes(ruleName)) {
        details.set(ruleName, { isValid: true, message: '' })
        continue
      }

      if (this.rules.hasOwnProperty(ruleName)) {
        let source: any
        if (this.rules[ruleName].source !== undefined) {
          source = this.rules[ruleName].source
        }

        if (Array.isArray(source)) {
          // conditional rules
          const fieldValues: { [key: string]: string } = {}
          source.map((field) => {
            fieldValues[field] = get(state, field)
          })
          const validationMethod = this.rules[ruleName].method
          const args = this.rules[ruleName].args || []
          if (!validationMethod(fieldValues, ...args)) {
            source.map((field) => {
              details.set(field, {
                isValid: false,
                message: this.rules[ruleName].message || '',
              })
            })
          } else {
            details.set(ruleName, { isValid: true, message: '' })
          }
        } else {
          const fieldValue = get(state, source || ruleName)
          const args = this.rules[ruleName].args || []
          const validationMethod = this.rules[ruleName].method

          if (!validationMethod(fieldValue, ...args)) {
            details.set(ruleName, {
              isValid: false,
              message: this.rules[ruleName].message || '',
            })
          } else {
            details.set(ruleName, { isValid: true, message: '' })
          }
        }
      }
    }
    return new ValidationResults(details)
  }

  public makeValid(): ValidationResults<T> {
    // Return a truthy validation result object
    // Use this to not show errors when the first time the form is opened
    const details: ValidationResultDetails<T> = new Map()

    for (const field in this.rules) {
      if (this.rules.hasOwnProperty(field)) {
        details.set(field, { isValid: true, message: '' })
      }
    }

    return new ValidationResults(details)
  }
}

export default FormValidator
